import React, { useRef } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Promptbar from '@/components/Promptbar/Promptbar';
import { Chat } from '@/components/chat/Chat';
import { Chatbar } from '@/components/chatbar/Chatbar';
import ChatTopbar from '@/components/redesign/chat/ChatTopbar';
import { TopNav } from '@/components/redesign';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ChatProvider, useChat } from '@/contexts/ChatContext';
import { DEFAULT_MODEL } from '@/libs/app/const';
import { calculateCredits } from '@/libs/helper';
import { getFollowupQuestions, sendQuery } from '@/libs/request';
import { t } from 'i18next';

const LoginReminder = dynamic(() => import('@/components/utils/LoginReminder'));

const ChatPageImpl = () => {
  const {
    state: {
      selectedConversation,
      messageIsStreaming,
      showPromptbar,
    },
    systemPrompt,
    handleSaveConversation,
    storageReady,
    storageBusy,
    files,
    setFiles,
    filesMeta,
    setFilesMeta,
    dispatch: homeDispatch,
    setNotification,
  } = useChat();
  const { currentUser, deductCredits } = useAuth();
  const stopConversationRef = useRef(false);

  const handleToggleContext = () => {
    homeDispatch({ field: 'showPromptbar', value: !showPromptbar });
    localStorage.setItem('showPromptbar', JSON.stringify(!showPromptbar));
  };

  const handleSend = async (message, deleteCount = 0) => {
    if (!storageReady || storageBusy) return false;
    if (
      !message.content.trim() ||
      files.some((file) => !filesMeta[file.name])
    ) {
      if (files.some((file) => !filesMeta[file.name])) {
        setNotification({
          status: 'error',
          msg: t('Please wait for file uploads to complete before sending'),
        });
      }
      return false;
    }

    const cost = calculateCredits({
      model: selectedConversation.model,
      baseModel:
        selectedConversation.model === 'medical-chat' ||
        selectedConversation.model === 'google-search'
          ? currentUser.model
          : selectedConversation.model,
      currentUser,
    });
    if (cost > currentUser.credits) {
      setNotification({
        status: 'error',
        msg: `You don't have enough credits to continue. Please recharge.`,
      });
      return false;
    }
    if (selectedConversation) {
      let updatedConversation;
      if (deleteCount) {
        const updatedMessages = [...selectedConversation.messages];
        for (let i = 0; i < deleteCount; i++) {
          updatedMessages.pop();
        }
        updatedConversation = {
          ...selectedConversation,
          messages: [...updatedMessages, message],
          followups: [],
        };
      } else {
        let newMessage = message;
        if (files.length > 0) {
          const filePayloads = files.map((file) => filesMeta[file.name]);
          newMessage.files = filePayloads;
          const values = Object.values(
            filePayloads.filter((file) => !file.type.startsWith('image')),
          );
          const totalCharacters =
            message.content.trim().length +
            values.reduce((sum, value) => {
              return sum + (value.content || '').length;
            }, 0);

          if (
            totalCharacters >
            parseInt(process.env.NEXT_PUBLIC_CHAT_WIDGET_INPUT_CHARACTERS_LIMIT)
          ) {
            setNotification({
              status: 'error',
              msg: `Total input message text and file content combined exceeds ${process.env.NEXT_PUBLIC_CHAT_WIDGET_INPUT_CHARACTERS_LIMIT} characters. Please delete some files or reduce the text content.`,
            });
            return false;
          }
        }
        setFiles([]);
        setFilesMeta({});
        updatedConversation = {
          ...selectedConversation,
          messages: [...selectedConversation.messages, newMessage],
          followups: [],
        };
      }
      homeDispatch({
        field: 'selectedConversation',
        value: updatedConversation,
      });
      homeDispatch({ field: 'loading', value: true });
      homeDispatch({ field: 'messageIsStreaming', value: true });
      let answerComplete = false;
      try {
        const chatBody = {
          systemPrompt,
          mode: updatedConversation.mode,
          messages: updatedConversation.messages.filter(
            (message) => message.type !== 'pubmed',
          ),
          model: updatedConversation.model,
          temperature: updatedConversation.temperature,
        };
        const controller = new AbortController();

        const response = await sendQuery(chatBody, controller.signal);

        if (!response.ok) {
          const payload = await response
            .json()
            .catch(() => ({ message: t('Something went wrong. Please try again.') }));
          setNotification({
            status: 'error',
            msg: payload.message,
          });
          return false;
        }
        const data = response.body;
        if (!data) {
          return false;
        }

        if (updatedConversation.messages.length === 1) {
          const { content } = message;
          const customName =
            content.length > 30 ? content.substring(0, 30) + '...' : content;
          updatedConversation = {
            ...updatedConversation,
            name: customName,
          };
        }
        homeDispatch({ field: 'loading', value: false });
        const reader = data.getReader();
        const decoder = new TextDecoder();
        let done = false;
        let isFirst = true;
        let text = '';
        while (!done) {
          if (stopConversationRef.current === true) {
            controller.abort();
            if (updatedConversation.model === DEFAULT_MODEL) {
              updatedConversation = {
                ...updatedConversation,
                messages: updatedConversation.messages.map((item, index) =>
                  index === updatedConversation.messages.length - 1 &&
                  item.role === 'assistant'
                    ? { ...item, incomplete: true }
                    : item,
                ),
              };
              homeDispatch({
                field: 'selectedConversation',
                value: updatedConversation,
              });
              await handleSaveConversation(updatedConversation);
              // Cancellation must not fall through to completion accounting.
              return false;
            }
            done = true;
            break;
          }
          const { value, done: doneReading } = await reader.read();
          done = doneReading;
          const chunkValue = decoder.decode(value);
          text += chunkValue;
          if (isFirst) {
            isFirst = false;
            const updatedMessages = [
              ...updatedConversation.messages,
              {
                role: 'assistant',
                content: chunkValue,
                type: 'chat',
                query: message.content,
              },
            ];
            updatedConversation = {
              ...updatedConversation,
              messages: updatedMessages,
            };
            homeDispatch({
              field: 'selectedConversation',
              value: updatedConversation,
            });
          } else {
            const updatedMessages = updatedConversation.messages.map(
              (message, index) => {
                if (index === updatedConversation.messages.length - 1) {
                  return {
                    ...message,
                    content: text,
                  };
                }
                return message;
              },
            );
            updatedConversation = {
              ...updatedConversation,
              messages: updatedMessages,
            };
            homeDispatch({
              field: 'selectedConversation',
              value: updatedConversation,
            });
          }
        }
        answerComplete = true;
        await handleSaveConversation(updatedConversation);
        homeDispatch({ field: 'messageIsStreaming', value: false });
        if (updatedConversation.model === DEFAULT_MODEL) {
          try {
            const hints = await getFollowupQuestions({
              messages: updatedConversation.messages.filter(
                (message) => message.type !== 'pubmed',
              ),
              mode: updatedConversation.mode,
              temperature: updatedConversation.temperature,
              answer: text,
              default_lang: t('lang'),
            });
            updatedConversation = {
              ...updatedConversation,
              followups: hints,
            };
            await handleSaveConversation(updatedConversation, { onlyIfPresent: true });
          } catch (e) {
            // Follow-up suggestions are optional; the answer is already shown.
          }
        }
        deductCredits(cost);
      } catch (error) {
        if (updatedConversation.model !== DEFAULT_MODEL || answerComplete) {
          throw error;
        }
        updatedConversation = {
          ...updatedConversation,
          messages: updatedConversation.messages.map((item, index) =>
            index === updatedConversation.messages.length - 1 &&
            item.role === 'assistant'
              ? { ...item, incomplete: true }
              : item,
          ),
        };
        homeDispatch({
          field: 'selectedConversation',
          value: updatedConversation,
        });
        setNotification({
          status: 'error',
          msg: t('This answer was interrupted and is incomplete. Please retry.'),
        });
        return false;
      } finally {
        // Completed answers already released the stream before awaiting follow-ups.
        if (!answerComplete) {
          homeDispatch({ field: 'loading', value: false });
          homeDispatch({ field: 'messageIsStreaming', value: false });
        }
      }
    }
    return true;
  };

  return (
    <>
      {selectedConversation && (
        <div className="flex h-screen overflow-hidden bg-white">
          {/* Left sidebar */}
          <Chatbar />

          {/* Main conversation area */}
          <main className="flex min-w-0 flex-1 flex-col">
            <ChatTopbar
              onToggleContext={handleToggleContext}
              contextOpen={showPromptbar}
            />
            <div className="flex min-h-0 flex-1">
              <Chat
                handleSend={handleSend}
                stopConversationRef={stopConversationRef}
              />
            </div>
          </main>

          {/* Right panel */}
          <Promptbar handleSend={handleSend} />

          <ReactTooltip
            id="general-chat"
            variant="dark"
            effect="solid"
            className="!z-50 !px-1.5 !py-1 !text-xs"
          />
        </div>
      )}
    </>
  );
};

export default function ChatPage({ currentUser }) {
  const title = 'AI Chatbot for Medical and Veterinary Questions';
  const description =
    'Chat with our AI for medical & veterinary advice. Get personalized, source-backed answers instantly.';
  const canonical = 'https://medical.chat-data.com/chat';
  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:site_name" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://medical.chat-data.com/demos/chatbot_en.png"
        />
        <meta property="og:image:width" content="1842" />
        <meta property="og:image:height" content="1204" />
        <meta property="og:image:alt" content={title} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@truechatdata" />
        <meta name="twitter:creator" content="@truechatdata" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta
          name="twitter:image"
          content="https://medical.chat-data.com/demos/chatbot_en.png"
        />
        <meta name="twitter:image:width" content="1842" />
        <meta property="twitter:image:height" content="1204" />
        <meta name="twitter:image:alt" content={title} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@graph': [
                {
                  '@type': 'BreadcrumbList',
                  '@id': `${canonical}/#breadcrumblist`,
                  itemListElement: [
                    {
                      '@type': 'ListItem',
                      '@id': 'https://medical.chat-data.com/#listItem',
                      position: 1,
                      name: 'Medical Chat | Medical AI Assistant',
                      item: 'https://medical.chat-data.com/',
                      nextItem: {
                        '@id': `${canonical}/#listItem`,
                      },
                    },
                    {
                      '@type': 'ListItem',
                      '@id': `${canonical}/#listItem`,
                      position: 2,
                      name: title,
                      item: `${canonical}/`,
                      previousItem: {
                        '@id': 'https://medical.chat-data.com/#listItem',
                      },
                    },
                  ],
                },
                {
                  '@type': 'WebPage',
                  '@id': `${canonical}/#webpage`,
                  url: canonical,
                  name: title,
                  description,
                  inLanguage: 'en-US',
                  isPartOf: {
                    '@id': 'https://medical.chat-data.com/#website',
                  },
                  breadcrumb: {
                    '@id': `${canonical}/#breadcrumblist`,
                  },
                },
              ],
            }),
          }}
        ></script>
      </Head>
      <ChatProvider
        serverPrompts={currentUser ? currentUser.prompts : undefined}
        serverPromptFolders={
          currentUser ? currentUser.promptFolders : undefined
        }
      >
        <AuthProvider user={currentUser}>
          {currentUser ? (
            <ChatPageImpl />
          ) : (
            <>
              <TopNav authed={!!currentUser} />
              <LoginReminder />
            </>
          )}
        </AuthProvider>
      </ChatProvider>
    </>
  );
}

export async function getServerSideProps(context) {
  const { req } = context;
  if (!req.isAuthenticated()) {
    return {
      props: {
        currentUser: null,
      },
    };
  }
  const { getCurrentUser } = await import('@/libs/helper');
  const currentUser = getCurrentUser(req.user);
  return {
    props: {
      currentUser,
    },
  };
}
